import React, { useEffect, useRef, useState } from 'react';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';

interface Msg { id: string; scope: string; threadId: string | null; senderBookingId: string; senderName: string; mine: boolean; body: string; kind: string; metadata: any; createdAt: string }
interface Mate { bookingId: string; name: string; chair: string | null; threadId: string | null; unread: number; blocked: boolean; blockedByMe: boolean }

interface Props {
  bookingId: string;
  tableId: string;
  userId: string;
  onClose: () => void;
  onUnreadChange?: (n: number) => void;
}

type View = { kind: 'roster' } | { kind: 'group' } | { kind: 'dm'; threadId: string; otherName: string; otherBookingId: string };

/**
 * Table Chat — guests at the same table chatting with each other. A roster of everyone who scanned
 * (auto-listed), a group thread, and private 1:1 DMs. DMs send instantly; block/report are the safety
 * net. Everything is ephemeral (purged when the table is cleared).
 */
const TableChat: React.FC<Props> = ({ bookingId, tableId, userId, onClose, onUnreadChange }) => {
  const [view, setView] = useState<View>({ kind: 'roster' });
  const [mates, setMates] = useState<Mate[]>([]);
  const [groupUnread, setGroupUnread] = useState(0);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [blocked, setBlocked] = useState<{ blocked: boolean; byMe: boolean }>({ blocked: false, byMe: false });
  const [menuOpen, setMenuOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  const totalUnread = groupUnread + mates.reduce((a, m) => a + (m.unread || 0), 0);
  useEffect(() => { onUnreadChange?.(totalUnread); }, [totalUnread, onUnreadChange]);

  const loadRoster = () => {
    apiService.tableChatRoster(bookingId).then((r) => {
      const d = (r as any)?.data; if (!d) return;
      setMates(d.mates || []);
      setGroupUnread(d.groupUnread || 0);
    }).catch(() => {});
  };

  useEffect(() => { loadRoster(); const iv = setInterval(loadRoster, 12000); return () => clearInterval(iv); /* eslint-disable-next-line */ }, [bookingId]);

  // Realtime: group messages on the table channel; DM messages on the open thread channel.
  useEffect(() => {
    let unsub = () => {};
    try {
      unsub = subscribeToChannel(`table_chat:${tableId}`, ['table_chat:group'], (_e, data) => {
        setView((v) => { if (v.kind === 'group' && data?.id) setMsgs((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, { ...data, mine: data.senderBookingId === bookingId }])); return v; });
        loadRoster();
      });
    } catch {}
    return () => { try { unsub(); } catch {} };
    // eslint-disable-next-line
  }, [tableId, bookingId]);

  useEffect(() => {
    if (view.kind !== 'dm') return;
    let unsub = () => {};
    try {
      unsub = subscribeToChannel(`table_dm:${view.threadId}`, ['table_chat:dm'], (_e, data) => {
        if (data?.body === '__blocked__') { setBlocked({ blocked: true, byMe: false }); return; }
        if (data?.body === '__unblocked__') { setBlocked({ blocked: false, byMe: false }); return; }
        if (data?.id) setMsgs((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, { ...data, mine: data.senderBookingId === bookingId }]));
      });
    } catch {}
    return () => { try { unsub(); } catch {} };
    // eslint-disable-next-line
  }, [view, bookingId]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs]);

  const openGroup = async () => {
    setView({ kind: 'group' }); setMsgs([]);
    const r = await apiService.tableChatGroup(bookingId).catch(() => null);
    setMsgs(((r as any)?.data || []) as Msg[]);
    apiService.tableChatMarkGroupRead(bookingId).then(loadRoster).catch(() => {});
  };

  const openDm = async (mate: Mate) => {
    const r = await apiService.tableChatOpenDm(bookingId, mate.bookingId).catch(() => null);
    const threadId = (r as any)?.data?.threadId; if (!threadId) return;
    setView({ kind: 'dm', threadId, otherName: mate.name, otherBookingId: mate.bookingId });
    setMsgs([]);
    const list = await apiService.tableChatDmList(bookingId, threadId).catch(() => null);
    const d = (list as any)?.data;
    setMsgs((d?.messages || []) as Msg[]);
    setBlocked({ blocked: !!d?.blocked, byMe: !!d?.blockedByMe });
    loadRoster();
  };

  const send = async () => {
    const text = draft.trim(); if (!text || sending) return;
    setSending(true); setDraft('');
    try {
      if (view.kind === 'group') { const r = await apiService.tableChatPostGroup(bookingId, text); appendMine(r); }
      else if (view.kind === 'dm') { const r = await apiService.tableChatDmSend(bookingId, view.threadId, text); appendMine(r); }
    } catch (e: any) {
      setNotice(e?.message || 'Could not send'); setDraft(text);
    } finally { setSending(false); }
  };
  const appendMine = (r: any) => { const m = r?.data as Msg; if (m?.id) setMsgs((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m])); };

  const shareTab = async () => {
    try { const r = await apiService.tableChatShareTab(bookingId); appendMine(r); }
    catch (e: any) { setNotice(e?.message || 'Start a group tab first'); }
  };

  const joinTab = async (code: string) => {
    try { await apiService.joinGroupTab(bookingId, code); setNotice(`Joined tab ${code} ✓`); }
    catch (e: any) { setNotice(e?.message || 'Could not join'); }
  };

  const toggleBlock = async () => {
    if (view.kind !== 'dm') return;
    const next = !blocked.blocked;
    try { await apiService.tableChatDmBlock(bookingId, view.threadId, next); setBlocked({ blocked: next, byMe: next }); setMenuOpen(false); loadRoster(); }
    catch (e: any) { setNotice(e?.message || 'Failed'); }
  };
  const report = async () => {
    if (view.kind !== 'dm') return;
    try { await apiService.tableChatDmReport(bookingId, view.threadId); setNotice('Reported to staff — thank you'); setMenuOpen(false); }
    catch (e: any) { setNotice(e?.message || 'Failed'); }
  };

  useEffect(() => { if (!notice) return; const t = setTimeout(() => setNotice(''), 3500); return () => clearTimeout(t); }, [notice]);

  const title = view.kind === 'group' ? '💬 Table chat' : view.kind === 'dm' ? view.otherName : 'Chat with your table';

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={S.head}>
          {view.kind !== 'roster'
            ? <button style={S.iconBtn} onClick={() => { setView({ kind: 'roster' }); setMenuOpen(false); loadRoster(); }}>‹</button>
            : <span style={{ width: 34 }} />}
          <div style={S.title}>{title}</div>
          {view.kind === 'dm'
            ? <button style={S.iconBtn} onClick={() => setMenuOpen((o) => !o)}>⋯</button>
            : <button style={S.iconBtn} onClick={onClose}>✕</button>}
        </div>

        {menuOpen && view.kind === 'dm' && (
          <div style={S.menu}>
            <button style={S.menuItem} onClick={toggleBlock}>{blocked.blocked && blocked.byMe ? '🔓 Unblock' : '🚫 Block'}</button>
            <button style={S.menuItem} onClick={report}>⚑ Report to staff</button>
          </div>
        )}

        {notice && <div style={S.notice}>{notice}</div>}

        {view.kind === 'roster' && (
          <div style={S.body}>
            <button style={S.groupRow} onClick={openGroup}>
              <span style={S.groupIcon}>👥</span>
              <span style={{ flex: 1, textAlign: 'left' }}>
                <span style={S.rowName}>Everyone at the table</span>
                <span style={S.rowSub}>Group chat · share tab code, plan rounds</span>
              </span>
              {groupUnread > 0 && <span style={S.badge}>{groupUnread}</span>}
            </button>

            <div style={S.sectionLabel}>People at your table</div>
            {mates.length === 0 && <div style={S.empty}>No one else has scanned yet. When they do, they'll show up here.</div>}
            {mates.map((m) => (
              <button key={m.bookingId} style={S.mateRow} onClick={() => openDm(m)}>
                <span style={S.avatar}>{(m.name || 'G').charAt(0).toUpperCase()}</span>
                <span style={{ flex: 1, textAlign: 'left' }}>
                  <span style={S.rowName}>{m.name}{m.blocked ? ' · blocked' : ''}</span>
                  <span style={S.rowSub}>{m.chair || 'At your table'}</span>
                </span>
                {m.unread > 0 && <span style={S.badge}>{m.unread}</span>}
                <span style={S.chev}>›</span>
              </button>
            ))}
          </div>
        )}

        {(view.kind === 'group' || view.kind === 'dm') && (
          <>
            <div style={S.thread}>
              {msgs.length === 0 && <div style={S.empty}>{view.kind === 'group' ? 'Say hi to your table 👋' : `Start chatting with ${view.kind === 'dm' ? view.otherName : ''}`}</div>}
              {msgs.map((m) => (
                <div key={m.id} style={{ ...S.bubbleRow, justifyContent: m.mine ? 'flex-end' : 'flex-start' }}>
                  <div style={{ ...S.bubble, ...(m.mine ? S.bubbleMine : S.bubbleOther) }}>
                    {!m.mine && view.kind === 'group' && <div style={S.bubbleName}>{m.senderName}</div>}
                    <div>{m.body}</div>
                    {m.kind === 'tab_invite' && m.metadata?.code && !m.mine && (
                      <button style={S.joinBtn} onClick={() => joinTab(m.metadata.code)}>Join tab {m.metadata.code}</button>
                    )}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            {view.kind === 'dm' && blocked.blocked ? (
              <div style={S.blockedBar}>{blocked.byMe ? 'You blocked this chat. Unblock from the ⋯ menu to continue.' : 'This chat is blocked.'}</div>
            ) : (
              <div style={S.inputBar}>
                {view.kind === 'group' && <button style={S.tabBtn} onClick={shareTab} title="Share my group-tab code">🍹</button>}
                <input
                  style={S.input}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                  placeholder={view.kind === 'group' ? 'Message your table…' : 'Message…'}
                  maxLength={500}
                />
                <button style={S.sendBtn} onClick={send} disabled={sending || !draft.trim()}>{sending ? '…' : 'Send'}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 },
  sheet: { width: '100%', maxWidth: 480, height: '86vh', background: '#fff', borderRadius: '20px 20px 0 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 12px', borderBottom: '1px solid #eef1f5' },
  iconBtn: { width: 34, height: 34, borderRadius: 10, border: 'none', background: '#f1f5f9', color: '#0f172a', fontSize: 18, fontWeight: 800, cursor: 'pointer' },
  title: { flex: 1, textAlign: 'center', fontSize: '1rem', fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  menu: { position: 'absolute', right: 14, top: 54, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 8px 24px rgba(15,23,42,0.15)', zIndex: 5, overflow: 'hidden' },
  menuItem: { display: 'block', width: '100%', textAlign: 'left', background: '#fff', border: 'none', padding: '11px 16px', fontSize: '0.9rem', fontWeight: 600, color: '#0f172a', cursor: 'pointer' },
  notice: { background: '#fff7ed', color: '#b45309', fontWeight: 700, fontSize: '0.82rem', padding: '8px 14px', textAlign: 'center' },
  body: { flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 },
  sectionLabel: { fontSize: '0.72rem', fontWeight: 800, letterSpacing: 0.4, color: '#94a3b8', textTransform: 'uppercase', margin: '6px 2px 0' },
  empty: { color: '#94a3b8', fontSize: '0.88rem', textAlign: 'center', padding: '18px 12px' },
  groupRow: { display: 'flex', alignItems: 'center', gap: 12, background: 'linear-gradient(135deg,#fff4ed,#ffe9dc)', border: '1px solid rgba(255,107,53,0.25)', borderRadius: 16, padding: '13px 14px', cursor: 'pointer', width: '100%' },
  groupIcon: { fontSize: 22 },
  mateRow: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #eef1f5', borderRadius: 14, padding: '11px 14px', cursor: 'pointer', width: '100%' },
  avatar: { width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16 },
  rowName: { display: 'block', fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' },
  rowSub: { display: 'block', fontSize: '0.78rem', color: '#94a3b8' },
  badge: { background: '#ef4444', color: '#fff', fontWeight: 800, fontSize: '0.72rem', minWidth: 20, height: 20, borderRadius: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px' },
  chev: { color: '#cbd5e1', fontSize: 18, fontWeight: 800 },
  thread: { flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, background: '#f8fafc' },
  bubbleRow: { display: 'flex' },
  bubble: { maxWidth: '78%', padding: '9px 12px', borderRadius: 16, fontSize: '0.92rem', lineHeight: 1.35, wordBreak: 'break-word' },
  bubbleMine: { background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', borderBottomRightRadius: 5 },
  bubbleOther: { background: '#fff', color: '#0f172a', border: '1px solid #e8edf3', borderBottomLeftRadius: 5 },
  bubbleName: { fontSize: '0.72rem', fontWeight: 800, color: '#e0531f', marginBottom: 2 },
  joinBtn: { marginTop: 8, background: '#0f172a', color: '#fff', border: 'none', borderRadius: 10, padding: '7px 12px', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer' },
  inputBar: { display: 'flex', alignItems: 'center', gap: 8, padding: 10, borderTop: '1px solid #eef1f5', background: '#fff' },
  tabBtn: { width: 40, height: 40, borderRadius: 12, border: '1px solid #ffd8c2', background: '#fff4ed', fontSize: 18, cursor: 'pointer' },
  input: { flex: 1, background: '#f1f5f9', border: 'none', borderRadius: 12, padding: '12px 14px', fontSize: '0.95rem', outline: 'none' },
  sendBtn: { background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 12, padding: '0 18px', height: 42, fontWeight: 800, cursor: 'pointer' },
  blockedBar: { padding: '14px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem', fontWeight: 600, background: '#fff', borderTop: '1px solid #eef1f5' },
};

export default TableChat;
